export type Plan = "free" | "starter" | "pro" | "studio";

export type Platform = "twitter" | "bluesky" | "linkedin" | "newsletter" | "short_video";

export type VoiceMood =
  | "default"
  | "excited"
  | "serious"
  | "playful"
  | "contemplative";

export type VariantLength = "short" | "medium" | "long";
export type VariantTone = "professional" | "neutral" | "casual";
export type VariantModel =
  | "auto"
  | "gpt-4o-mini"
  | "gpt-4o"
  | "gpt-4.1"
  | "gpt-4.1-mini"
  | "gpt-4.1-nano"
  | "gpt-5"
  | "gpt-5-mini"
  | "gpt-5-nano"
  | "o3"
  | "o3-mini"
  | "o4-mini"
  | "workers-ai-llama-3.3-70b";

export const PLAN_RANK: Record<Plan, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  studio: 3,
};

export const MODEL_MIN_PLAN: Record<VariantModel, Plan> = {
  auto: "free",
  "workers-ai-llama-3.3-70b": "free",

  "gpt-4o-mini": "starter",
  "gpt-4.1-nano": "starter",
  "gpt-5-nano": "starter",
  "gpt-4.1-mini": "starter",
  "gpt-5-mini": "starter",

  "gpt-4o": "pro",
  "gpt-4.1": "pro",
  "gpt-5": "pro",
  "o3-mini": "pro",
  "o4-mini": "pro",

  o3: "studio",
};

export function isModelAllowed(model: VariantModel, plan: Plan): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[MODEL_MIN_PLAN[model]];
}

export type ProjectStatus = "queued" | "generating" | "ready" | "failed";

export type VoiceProfileStatus = "training" | "ready" | "failed";

export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid";

export const PLAN_CREDITS: Record<Plan, number> = {
  free: 0,
  starter: 30,
  pro: 100,
  studio: 400,
};

export const PLAN_PRICES_USD: Record<Plan, number> = {
  free: 0,
  starter: 12,
  pro: 29,
  studio: 79,
};

export const PLATFORMS: Platform[] = [
  "twitter",
  "bluesky",
  "linkedin",
  "newsletter",
  "short_video",
];

export interface User {
  id: string;
  email: string;
  name: string | null;
  created_at: number;
  email_verified_at: number | null;
}

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  plan: Plan;
  status: SubscriptionStatus;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: number;
  created_at: number;
  updated_at: number;
}

export interface VoiceProfile {
  id: string;
  user_id: string;
  name: string;
  profile_json: string;
  status: VoiceProfileStatus;
  source_count: number;
  created_at: number;
  updated_at: number;
}

export interface Project {
  id: string;
  user_id: string;
  voice_profile_id: string | null;
  title: string | null;
  input_text: string;
  status: ProjectStatus;
  created_at: number;
  updated_at: number;
}

export interface Variant {
  id: string;
  project_id: string;
  platform: Platform;
  content: string;
  content_edited: string | null;
  edit_count: number;
  generation_meta: string | null;
  created_at: number;
  updated_at: number;
}
